import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSubscriptionPlans,
  updateSubscriptionPlanModules,
  updateSubscriptionPlanBrands,
  syncStripeProducts,
  getAllBrands,
  SubscriptionPlan,
  Brand,
  SyncStripeProductsResponse,
} from "../services/accountService";

/**
 * Query key for subscription plans
 */
const SUBSCRIPTION_PLANS_KEY = ["subscriptionPlans"];

/**
 * Hook to fetch all subscription plans
 *
 * @returns React Query result with plans data, loading, and error states
 */
export function useSubscriptionPlans() {
  return useQuery<SubscriptionPlan[], Error>({
    queryKey: SUBSCRIPTION_PLANS_KEY,
    queryFn: getSubscriptionPlans,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update subscription plan modules
 *
 * Uses optimistic updates for better UX.
 * Automatically refetches plans after successful update.
 *
 * @returns React Query mutation with mutate function and status
 */
export function useUpdatePlanModules() {
  const queryClient = useQueryClient();

  return useMutation<
    SubscriptionPlan,
    Error,
    { planId: string; modules: string[] },
    { previousPlans?: SubscriptionPlan[] }
  >({
    mutationFn: ({ planId, modules }) =>
      updateSubscriptionPlanModules(planId, modules),

    // Optimistic update
    onMutate: async ({ planId, modules }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: SUBSCRIPTION_PLANS_KEY });

      // Snapshot previous value
      const previousPlans = queryClient.getQueryData<SubscriptionPlan[]>(
        SUBSCRIPTION_PLANS_KEY
      );

      // Optimistically update to the new value
      queryClient.setQueryData<SubscriptionPlan[]>(
        SUBSCRIPTION_PLANS_KEY,
        (old) => {
          if (!old) return old;
          return old.map((plan) =>
            plan.id === planId ? { ...plan, includedModules: modules } : plan
          );
        }
      );

      // Return context with snapshot
      return { previousPlans };
    },

    // If mutation fails, rollback to previous value
    onError: (err, variables, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(SUBSCRIPTION_PLANS_KEY, context.previousPlans);
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_PLANS_KEY });
    },
  });
}

/**
 * Hook to update subscription plan brands
 *
 * Uses optimistic updates for better UX.
 * Automatically refetches plans after successful update.
 *
 * @returns React Query mutation with mutate function and status
 */
export function useUpdatePlanBrands() {
  const queryClient = useQueryClient();

  return useMutation<
    SubscriptionPlan,
    Error,
    { planId: string; brandIds: string[] },
    { previousPlans?: SubscriptionPlan[] }
  >({
    mutationFn: ({ planId, brandIds }) =>
      updateSubscriptionPlanBrands(planId, brandIds),

    // Optimistic update
    onMutate: async ({ planId, brandIds }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: SUBSCRIPTION_PLANS_KEY });

      // Snapshot previous value
      const previousPlans = queryClient.getQueryData<SubscriptionPlan[]>(
        SUBSCRIPTION_PLANS_KEY
      );

      // Get brand objects from cache
      const allBrands = queryClient.getQueryData<Brand[]>(["brands"]);

      // Optimistically update to the new value
      queryClient.setQueryData<SubscriptionPlan[]>(
        SUBSCRIPTION_PLANS_KEY,
        (old) => {
          if (!old) return old;
          return old.map((plan) => {
            if (plan.id === planId && allBrands) {
              const newBrands = allBrands.filter((b) =>
                brandIds.includes(b.id)
              );
              return { ...plan, brands: newBrands };
            }
            return plan;
          });
        }
      );

      // Return context with snapshot
      return { previousPlans };
    },

    // If mutation fails, rollback to previous value
    onError: (err, variables, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(SUBSCRIPTION_PLANS_KEY, context.previousPlans);
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_PLANS_KEY });
    },
  });
}

/**
 * Hook to fetch all brands
 *
 * @returns React Query result with brands data, loading, and error states
 */
export function useBrands() {
  return useQuery<Brand[], Error>({
    queryKey: ["brands"],
    queryFn: getAllBrands,
    staleTime: 10 * 60 * 1000, // 10 minutes (brands rarely change)
  });
}

/**
 * Hook to sync Stripe products
 *
 * Automatically refetches plans after successful sync.
 *
 * @returns React Query mutation with mutate function and status
 */
export function useSyncStripeProducts() {
  const queryClient = useQueryClient();

  return useMutation<SyncStripeProductsResponse, Error>({
    mutationFn: syncStripeProducts,

    // Refetch plans after successful sync
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_PLANS_KEY });
    },
  });
}
